import { Box, Stack, useTheme } from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useMultiHangarState } from "../../containers/MultiHangarContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { LocationsContainer } from "../../containers/LocationsStateContainer";
import { Tenant } from "../../types";
import { CollapsibleRampActionCard } from "../Ramp/CollapsibleRampActionCard";
import { HoldingAreaList } from "./HoldingAreaList";
import { HoldingAreaPresenter } from "./HoldingAreaPresenter";
import { LayoutToolbarWrapper } from "./LayoutToolbarWrapper";
import { MultiHangarInfoCard } from "./MultiHangarInfoCard";
import { AssignAircraftDialog } from "./AssignAircraftDialog";
import { EditTenant } from "../../widgets/EditTenant";
import { useHoldingArea } from "../../hooks/useHoldingArea";
import { HangarLeftPanelWrapper } from "./HangarLeftPanelWrapper";
import { useApi } from "../../containers/ApiContainer";
import { TenantService } from "../../services/TenantService";

export const HOLDING_AREA_ID = "holding-area";

/**
 * Helper to add tenants to holding area with null positions
 */
const addTenantsToHoldingArea = (
  hangars: any[],
  tenants: Tenant | Tenant[]
): any[] => {
  const tenantArray = Array.isArray(tenants) ? tenants : [tenants];
  const tenantsWithNullPosition = tenantArray.map((t) => ({
    ...t,
    position: { ...t.position, x: null, y: null, angle: null },
  }));

  return hangars.map((h) =>
    h.id === HOLDING_AREA_ID
      ? {
          ...h,
          stack: {
            ...h.stack,
            tenants: [...tenantsWithNullPosition, ...h.stack.tenants],
          },
        }
      : h
  );
};

/**
 * Helper to move tenant from holding area to target hangar
 */
const moveTenantsFromHoldingAreaToHangar = (
  hangars: any[],
  tenant: Tenant,
  targetHangarId: string
): any[] => {
  return hangars.map((h) => {
    if (h.id === targetHangarId) {
      // Add to target hangar with null position (unplaced)
      return {
        ...h,
        stack: {
          ...h.stack,
          tenants: [
            ...h.stack.tenants,
            {
              ...tenant,
              position: {
                ...tenant.position,
                x: null,
                y: null,
                angle: null,
                stack_id: h.stack.id,
              },
            },
          ],
        },
      };
    } else if (h.id === HOLDING_AREA_ID) {
      // Remove from holding area
      return {
        ...h,
        stack: {
          ...h.stack,
          tenants: h.stack.tenants.filter((t) => t.id !== tenant.id),
        },
      };
    }
    return h;
  });
};

type Props = {
  feetToPixels: number;
  multiHangarLayoutHeight: number;
};

export const HoldingArea: React.FC<Props> = ({
  feetToPixels,
  multiHangarLayoutHeight,
}) => {
  const { activeFBO } = useActiveFBOs();
  const theme = useTheme();
  const { focus, activeLocationId, setActiveLocationId, hangars, setHangars } =
    useMultiHangarState();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const holdingAreaTracking = useHoldingArea();
  const { postgrest } = useApi();
  const tenantService = React.useMemo(() => new TenantService(postgrest), [postgrest]);

  const holdingArea = hangars.find((h) => h.id === HOLDING_AREA_ID);

  // Check if we should use the list-based UI (default: true)
  const useListView = activeFBO?.settings?.useListHoldingArea ?? true;

  // Dialog state
  const [assignDialogOpen, setAssignDialogOpen] = React.useState(false);
  const [selectedTenantForAssignment, setSelectedTenantForAssignment] =
    React.useState<Tenant | null>(null);
  const [isAssigning, setIsAssigning] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [selectedTenantForEdit, setSelectedTenantForEdit] =
    React.useState<Tenant | null>(null);

  if (!holdingArea || !holdingArea.stack) {
    return null;
  }

  // Handler for opening assignment dialog
  const handleAssign = React.useCallback(
    (tenantId: string) => {
      const tenant = holdingArea.stack.tenants.find((t) => t.id === tenantId);
      if (tenant) {
        setSelectedTenantForAssignment(tenant);
        setAssignDialogOpen(true);
      }
    },
    [holdingArea.stack.tenants]
  );

  // Handler for adding aircraft
  const handleAddAircraft = React.useCallback(
    async (tenant: Tenant | Tenant[]) => {
      setHangars((hangars) => addTenantsToHoldingArea(hangars, tenant));

      // Create tenant in database FIRST, then track in holding area
      try {
        const tenantArray = Array.isArray(tenant) ? tenant : [tenant];

        for (const t of tenantArray) {
          // Step 1: Upsert tenant into database (handles both new and existing)
          await tenantService.createTenant(t);

          // Step 2: Track in holding area (now tenant definitely exists in DB)
          await holdingAreaTracking.addTenant(t);
        }
      } catch (err) {
        console.error("Failed to save tenant to holding area:", err);
        snackbar.notify({
          text: "Failed to save aircraft to holding area",
          severity: "error",
        });
      }
    },
    [setHangars, holdingAreaTracking, snackbar, tenantService]
  );

  // Handler for editing aircraft
  const handleEdit = React.useCallback((tenant: Tenant) => {
    setSelectedTenantForEdit(tenant);
    setEditDialogOpen(true);
  }, []);

  // Handler for removing aircraft
  const handleRemove = React.useCallback(
    async (tenantId: string) => {
      const tenant = holdingArea.stack.tenants.find((t) => t.id === tenantId);

      setHangars((hangars) =>
        hangars.map((h) =>
          h.id === HOLDING_AREA_ID
            ? {
                ...h,
                stack: {
                  ...h.stack,
                  tenants: h.stack.tenants.filter((t) => t.id !== tenantId),
                },
              }
            : h
        )
      );

      // Update tracking in database
      try {
        await holdingAreaTracking.removeTenant(tenantId);
      } catch (err) {
        console.error("Failed to update holding area tracking:", err);
        snackbar.notify({
          text: "Failed to update holding area tracking",
          severity: "warning",
        });
      }

      if (tenant) {
        snackbar.notify({
          text: `${tenant.tail_number || tenant.aircraft.model} removed from holding area`,
          severity: "success",
        });
      }
    },
    [holdingArea.stack.tenants, setHangars, snackbar, holdingAreaTracking]
  );

  // Handler for confirming assignment
  const handleConfirmAssignment = React.useCallback(
    async (targetHangarId: string) => {
      if (!selectedTenantForAssignment) return;

      setIsAssigning(true);

      try {
        // Find target hangar for success message
        const targetHangar = hangars.find((h) => h.id === targetHangarId);

        // Move tenant from holding area to target hangar
        setHangars((hangars) =>
          moveTenantsFromHoldingAreaToHangar(
            hangars,
            selectedTenantForAssignment,
            targetHangarId
          )
        );

        // Update tracking in database
        try {
          await holdingAreaTracking.assignTenant(selectedTenantForAssignment.id);
        } catch (err) {
          console.error("Failed to update holding area tracking:", err);
          snackbar.notify({
            text: "Failed to update holding area tracking",
            severity: "warning",
          });
        }

        // Show success notification
        if (targetHangar) {
          snackbar.notify({
            text: `${selectedTenantForAssignment.tail_number || selectedTenantForAssignment.aircraft.model} assigned to ${targetHangar.name}`,
            severity: "success",
          });
        }

        // Close dialog
        setAssignDialogOpen(false);
        setSelectedTenantForAssignment(null);
      } finally {
        setIsAssigning(false);
      }
    },
    [selectedTenantForAssignment, setHangars, hangars, snackbar, holdingAreaTracking]
  );

  // Get available hangars (exclude holding area)
  const availableHangars = React.useMemo(
    () => hangars.filter((h) => h.id !== HOLDING_AREA_ID),
    [hangars]
  );

  // Render canvas-based UI (old version)
  if (!useListView) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        onClick={(_e) => {
          // if the user is focused on one of the hangars, keep the selected hangar blue
          if (focus) {
            return;
          }
          setActiveLocationId(HOLDING_AREA_ID);
        }}
        sx={{
          pr: 1,
          mt: "68px",
          height: "calc(100vh - 120px)",
          width: 250,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            zIndex: 1000,
            top: 0,
            left: 72,
            display: activeLocationId === HOLDING_AREA_ID ? "block" : "none",
          }}
        >
          <CollapsibleRampActionCard
            hideTitle
            location={{ type: "hangar", ...holdingArea }}
            onClickBack={() => navigate("/hangars")}
          >
            <Stack
              direction="column"
              display="flex"
              flex={1}
              spacing={1}
              height="98%"
            >
              <MultiHangarInfoCard />
              <HangarLeftPanelWrapper
                hangarId={HOLDING_AREA_ID}
                width={300}
                viewableHangars={hangars}
                activeHangarId={activeLocationId}
                setActiveHangarId={setActiveLocationId}
                // none of this actually gets used for the holding area so we just stub it out
                stacks={[]}
                setStacks={() => null}
                stackStrategy={null}
                setStackStrategy={() => null}
                customPlacementOptions={{
                  wall_spacing_left: 0,
                  wall_spacing_back: 0,
                  wall_spacing_right: 0,
                  horizontal_spacing: 0,
                  vertical_spacing: 0,
                  overlap: false,
                  ignore_nose_in_out: false,
                  ignore_front_back: false,
                  ignore_outside_hangar: false,
                  corner_strategies: [],
                  cloud_position_prioritization: false,
                  xy_weights: [],
                  towing_equipment_id: "",
                }}
                setCustomPlacementOptions={() => null}
              />
            </Stack>
          </CollapsibleRampActionCard>
        </Box>
        <Box
          sx={{
            position: "absolute",
            zIndex: 1000,
            top: 0,
            left: 475,
            maxWidth: "calc(100vw - 475px)",
            display: activeLocationId === HOLDING_AREA_ID ? "block" : "none",
          }}
        >
          <LayoutToolbarWrapper
            hangarId={HOLDING_AREA_ID}
            setStacks={() => null}
          />
        </Box>
        <Box sx={{ borderRadius: 2 }}>
          <HoldingAreaPresenter
            active={activeLocationId === HOLDING_AREA_ID}
            feetToPixels={feetToPixels}
            multiHangarDepth={
              (multiHangarLayoutHeight || 0) - parseFloat(theme.spacing(2))
            }
            customPlacementOptions={{
              wall_spacing_left: 0,
              wall_spacing_back: 0,
              wall_spacing_right: 0,
              horizontal_spacing: 0,
              vertical_spacing: 0,
              overlap: false,
              ignore_nose_in_out: false,
              ignore_front_back: false,
              ignore_outside_hangar: false,
              density_threshold: 0,
              tug_spacing: 0,
              sample_size: 0,
              corner_strategies: [],
              cloud_position_prioritization: false,
              xy_weights: [],
              towing_equipment_id: "",
            }}
          />
        </Box>
      </Stack>
    );
  }

  // Render list-based UI (new version)
  return (
    <Stack
      direction="column"
      alignItems="center"
      onClick={(_e) => {
        // if the user is focused on one of the hangars, keep the selected hangar blue
        if (focus) {
          return;
        }
        setActiveLocationId(HOLDING_AREA_ID);
      }}
      sx={{
        pr: 1,
        mt: "68px",
        height: "calc(100vh - 120px)",
        width: 350, // 40% wider than original 250
      }}
    >
      {/* Left sidebar panel */}
      <Box
        sx={{
          position: "absolute",
          zIndex: 1000,
          top: 0,
          left: 72,
          display: activeLocationId === HOLDING_AREA_ID ? "block" : "none",
        }}
      >
        <CollapsibleRampActionCard
          hideTitle
          location={{ type: "hangar", ...holdingArea }}
          onClickBack={() => navigate("/hangars")}
        >
          <Stack
            direction="column"
            display="flex"
            flex={1}
            spacing={1}
            height="98%"
          >
            <MultiHangarInfoCard />
            {/* Note: HangarLeftPanelWrapper not shown for holding area -
                individual hangar metrics don't make sense for staging area */}
          </Stack>
        </CollapsibleRampActionCard>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          position: "absolute",
          zIndex: 1000,
          top: 0,
          left: 475,
          maxWidth: "calc(100vw - 475px)",
          display: activeLocationId === HOLDING_AREA_ID ? "block" : "none",
        }}
      >
        <LayoutToolbarWrapper
          hangarId={HOLDING_AREA_ID}
          setStacks={() => null}
        />
      </Box>

      {/* Main content - list-based UI */}
      <HoldingAreaList
        tenants={holdingArea.stack.tenants}
        onAssign={handleAssign}
        onEdit={handleEdit}
        onRemove={handleRemove}
        onAdd={handleAddAircraft}
        setStack={(stack) => {
          setHangars((hangars) =>
            hangars.map((h) =>
              h.id === HOLDING_AREA_ID
                ? { ...h, stack: { ...h.stack, ...stack } }
                : h
            )
          );
        }}
        location={{ type: "hangar", ...holdingArea }}
      />

      {/* Assignment Dialog */}
      <AssignAircraftDialog
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          setSelectedTenantForAssignment(null);
        }}
        aircraftTail={selectedTenantForAssignment?.tail_number || ""}
        aircraftModel={selectedTenantForAssignment?.aircraft?.model || ""}
        aircraftWingspan={selectedTenantForAssignment?.aircraft?.wingspan}
        aircraftLength={selectedTenantForAssignment?.aircraft?.length}
        availableHangars={availableHangars}
        onAssign={handleConfirmAssignment}
        isAssigning={isAssigning}
      />

      {/* Edit Tenant Dialog */}
      {editDialogOpen && selectedTenantForEdit && (
        <LocationsContainer>
          <EditTenant
            tenant={selectedTenantForEdit}
            setTenant={(updatedTenant) => {
              setHangars((hangars) =>
                hangars.map((h) =>
                  h.id === HOLDING_AREA_ID
                    ? {
                        ...h,
                        stack: {
                          ...h.stack,
                          tenants: h.stack.tenants.map((t) =>
                            t.id === updatedTenant.id ? updatedTenant : t
                          ),
                        },
                      }
                    : h
                )
              );
            }}
            stack={holdingArea.stack}
            setStack={(stack) => {
              setHangars((hangars) =>
                hangars.map((h) =>
                  h.id === HOLDING_AREA_ID ? { ...h, stack } : h
                )
              );
            }}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedTenantForEdit(null);
            }}
          />
        </LocationsContainer>
      )}
    </Stack>
  );
};
